import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { Input, InputGroup } from 'components/input'
import { Select } from 'components/select'
import { Textarea } from 'components/textarea'
import { Switch } from 'components/switch'
import { Divider } from 'components/divider'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { FileAttachment } from 'components/custom/FileAttachment'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { GET_EXPLORE_ITEMS } from 'graphql/exploreItems/queries'
import { CREATE_EXPLORE_ITEM, UPDATE_EXPLORE_ITEM } from 'graphql/exploreItems/mutations'
import { t } from 'i18next'
import loadingImage from '../../../assets/images/boat-loading.gif'

// Default options - will be populated from API
const CATEGORY_OPTIONS = [
    { value: 'ATR', label: 'Attraction' },
    { value: 'HOT', label: 'Hotel' },
    { value: 'CAR', label: 'Car Rental' },
]

const COUNTRY_OPTIONS = [
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'OM', label: 'Oman' },
    { value: 'QA', label: 'Qatar' },
    { value: 'SA', label: 'Saudi Arabia' },
]

const CURRENCY_OPTIONS = [
    { value: 'AED', label: 'AED (UAE Dirham)' },
    { value: 'BHD', label: 'BHD (Bahraini Dinar)' },
    { value: 'KWD', label: 'KWD (Kuwaiti Dinar)' },
    { value: 'OMR', label: 'OMR (Omani Rial)' },
    { value: 'QAR', label: 'QAR (Qatari Riyal)' },
    { value: 'SAR', label: 'SAR (Saudi Riyal)' },
]

// Restricted currencies for now (BHD and QAR only)
const RESTRICTED_CURRENCY_OPTIONS = [
    { value: 'BHD', label: 'BHD (Bahraini Dinar)' },
    { value: 'QAR', label: 'QAR (Qatari Riyal)' },
]

export default function ExploreItemForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const { t } = useTranslation()
    const isEdit = Boolean(id)
    
    // Phone input constants (matching passengersForm.js pattern)
    const DEFAULT_COUNTRY = 'bh'
    const DEFAULT_COUNTRY_CODE = `${
        parsePhoneNumberFromString('1', DEFAULT_COUNTRY)?.country?.callingCode?.[0] || '973'
    }`

    // Dialog states
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [blockSubmission, setBlockSubmission] = useState(false)

    // Form state
    const [formData, setFormData] = useState({
        country_code: '',
        category_code: '',
        content: {
            title: { en: '', ar: '' },
            description: { en: '', ar: '' },
            link_url: '',
            contact_no: '',
            pricing: {
                price_from: '',
                currency: '',
            },
            location: {
                city: '',
                lat: '',
                lng: '',
            },
            cover_image: {
                mobile: null,
                tablet: null,
            },
            images: [],
        },
        valid_from: '',
        valid_to: '',
        is_active: true,
    })

    // File states
    const [coverImageMobile, setCoverImageMobile] = useState([])
    const [coverImageTablet, setCoverImageTablet] = useState([])
    const [additionalImages, setAdditionalImages] = useState([])

    // Validation errors state
    const [validationErrors, setValidationErrors] = useState({})

    // Dynamic options from API
    const [availableCategories, setAvailableCategories] = useState([...CATEGORY_OPTIONS])
    const [availableCountries, setAvailableCountries] = useState([...COUNTRY_OPTIONS])
    const [availableCurrencies, setAvailableCurrencies] = useState([...RESTRICTED_CURRENCY_OPTIONS])

    // Loading states
    const [loading, setLoading] = useState(false)

    // GraphQL hooks
    const [getExploreItems, { loading: getOptionsLoading }] = useLazyQuery(GET_EXPLORE_ITEMS, {
        onCompleted: data => {
            if (data.getExploreItems && data.getExploreItems.success) {
                const items = data.getExploreItems.explore_item || []
                // Extract unique categories, countries, and currencies from the data
                const uniqueCategories = new Set()
                const uniqueCountries = new Set()
                const uniqueCurrencies = new Set()
                
                items.forEach(item => {
                    if (item.category_code) {
                        uniqueCategories.add(item.category_code)
                    }
                    if (item.country_code) {
                        uniqueCountries.add(item.country_code)
                    }
                    if (item.content?.pricing?.currency) {
                        uniqueCurrencies.add(item.content.pricing.currency)
                    }
                })
                
                // Update available options - ensure base options are always included
                const categoryOptions = [
                    ...CATEGORY_OPTIONS,
                    ...Array.from(uniqueCategories)
                        .filter(code => !CATEGORY_OPTIONS.some(opt => opt.value === code))
                        .map(code => ({
                            value: code,
                            label: getCategoryLabel(code)
                        }))
                ]
                
                const countryOptions = [
                    ...COUNTRY_OPTIONS,
                    ...Array.from(uniqueCountries)
                        .filter(code => !COUNTRY_OPTIONS.some(opt => opt.value === code) && code !== 'AR')
                        .map(code => ({
                            value: code,
                            label: getCountryLabel(code)
                        }))
                ]
                
                const currencyOptions = [
                    ...RESTRICTED_CURRENCY_OPTIONS,
                    ...Array.from(uniqueCurrencies)
                        .filter(code => !RESTRICTED_CURRENCY_OPTIONS.some(opt => opt.value === code))
                        .map(code => ({
                            value: code,
                            label: code
                        }))
                ]
                
                // Always update with the merged options
                setAvailableCategories(categoryOptions)
                setAvailableCountries(countryOptions)
                setAvailableCurrencies(currencyOptions)
            }
        },
        onError: error => {
            console.error('Error fetching options:', error)
            // Ensure static options are available even if API fails
            setAvailableCategories([...CATEGORY_OPTIONS])
            setAvailableCountries([...COUNTRY_OPTIONS])
            setAvailableCurrencies([...RESTRICTED_CURRENCY_OPTIONS])
        },
    })

    const [getExploreItem, { loading: getLoading }] = useLazyQuery(GET_EXPLORE_ITEMS, {
        onCompleted: data => {
            if (data.getExploreItems && data.getExploreItems.success && data.getExploreItems.explore_item) {
                const item = data.getExploreItems.explore_item[0] // Get first item from array
                if (item) {
                    setFormData({
                        country_code: item.country_code,
                        category_code: item.category_code,
                        content: {
                            title: item.content.title || { en: '', ar: '' },
                            description: item.content.description || { en: '', ar: '' },
                            link_url: item.content.link_url || '',
                            contact_no: item.content.contact_no || '',
                            pricing: {
                                price_from: item.content.pricing?.price_from || '',
                                currency: item.content.pricing?.currency || 'BHD',
                            },
                            location: {
                                city: item.content.location?.city || '',
                                lat: item.content.location?.lat || '',
                                lng: item.content.location?.lng || '',
                            },
                            cover_image: item.content.cover_image || { mobile: null, tablet: null },
                            images: item.content.images || [],
                        },
                        valid_from: item.valid_from,
                        valid_to: item.valid_to,
                        is_active: item.is_active,
                    })

                    // Set existing cover images for file attachment components
                    if (item.content.cover_image) {
                        if (item.content.cover_image.mobile) {
                            setCoverImageMobile([item.content.cover_image.mobile])
                        }
                        if (item.content.cover_image.tablet) {
                            setCoverImageTablet([item.content.cover_image.tablet])
                        }
                    }

                    // Set existing additional images
                    if (item.content.images && item.content.images.length > 0) {
                        const existingImages = item.content.images.map(img => img.mobile || img.tablet).filter(Boolean)
                        setAdditionalImages(existingImages)
                    }
                }
            }
        },
    })

    const [createExploreItem] = useMutation(CREATE_EXPLORE_ITEM, {
        onCompleted: data => {
            setLoading(false)
            if (data.createExploreItem.success) {
                setSuccessMessage('Explore item created successfully')
                setShowSucDialog(true)
                setBlockSubmission(true)
                setTimeout(() => {
                    navigate('/explore-items')
                }, 2000)
            } else {
                setErrorMessage(data.createExploreItem.message || 'Failed to create explore item')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setLoading(false)
            setErrorMessage(error.message || 'An error occurred while creating the explore item')
            setShowErrDialog(true)
        },
    })

    const [updateExploreItem] = useMutation(UPDATE_EXPLORE_ITEM, {
        refetchQueries: [
            {
                query: GET_EXPLORE_ITEMS,
                variables: { 
                    itemId: id,
                    page: { limit: 1, offset: 0 }
                },
            }
        ],
        onCompleted: data => {
            setLoading(false)
            if (data.updateExploreItem.success) {
                setSuccessMessage('Explore item updated successfully')
                setShowSucDialog(true)
                setBlockSubmission(true)
                setTimeout(() => {
                    navigate('/explore-items')
                }, 2000)
            } else {
                setErrorMessage(data.updateExploreItem.message || 'Failed to update explore item')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setLoading(false)
            setErrorMessage(error.message || 'An error occurred while updating the explore item')
            setShowErrDialog(true)
        },
    })

    // Helper functions
    const getCategoryLabel = (categoryCode) => {
        const category = CATEGORY_OPTIONS.find(opt => opt.value === categoryCode)
        return category ? category.label : categoryCode
    }

    const getCountryLabel = (countryCode) => {
        const country = COUNTRY_OPTIONS.find(opt => opt.value === countryCode)
        return country ? country.label : countryCode
    }

    // Load options and data
    useEffect(() => {
        // Load available options from API
        getExploreItems({
            variables: {
                page: { limit: 100, offset: 0 }
            }
        })
        
        // Load data for edit mode
        if (isEdit && id) {
            getExploreItem({
                variables: { 
                    itemId: id,
                    page: { limit: 1, offset: 0 }
                },
            })
        }
    }, [isEdit, id, getExploreItem, getExploreItems])

    const handleInputChange = (field, value) => {
        if (field.startsWith('content.')) {
            const contentField = field.replace('content.', '')
            if (contentField.includes('.')) {
                const [parent, child] = contentField.split('.')
                setFormData(prev => ({
                    ...prev,
                    content: {
                        ...(prev.content || {}),
                        [parent]: {
                            ...(prev.content?.[parent] || {}),
                            [child]: value,
                        },
                    },
                }))
            } else {
                setFormData(prev => ({
                    ...prev,
                    content: {
                        ...(prev.content || {}),
                        [contentField]: value,
                    },
                }))
            }
        } else if (field.includes('.')) {
            const [parent, child] = field.split('.')
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent] || {}),
                    [child]: value,
                },
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }))
        }
    }

    const handleCoverImageMobileChange = (files, fileIndex) => {
        setCoverImageMobile(files)
        if (files.length > 0 && validationErrors.coverImageMobile) {
            setValidationErrors(prev => ({ ...prev, coverImageMobile: null }))
        }
    }

    const handleCoverImageTabletChange = (files, fileIndex) => {
        setCoverImageTablet(files)
        if (files.length > 0 && validationErrors.coverImageTablet) {
            setValidationErrors(prev => ({ ...prev, coverImageTablet: null }))
        }
    }

    const handleAdditionalImagesChange = (files, fileIndex) => {
        setAdditionalImages(files)
    }

    const validateForm = () => {
        const errors = {}

        // Helper function to safely trim string values
        const safeTrim = (value) => {
            return typeof value === 'string' ? value.trim() : value
        }

        // Validate country
        if (!safeTrim(formData.country_code)) {
            errors.country = 'Country is required'
        }

        // Validate category
        if (!safeTrim(formData.category_code)) {
            errors.category = 'Category is required'
        }

        // Validate English title
        if (!safeTrim(formData.content?.title?.en)) {
            errors.titleEn = 'English title is required'
        }

        // Validate Arabic title  
        if (!safeTrim(formData.content?.title?.ar)) {
            errors.titleAr = 'Arabic title is required'
        }

        // Validate cover images (only required for new items or if no existing images)
        if (coverImageMobile.length === 0 && (!isEdit || !formData.content?.cover_image?.mobile)) {
            errors.coverImageMobile = 'Mobile cover image is required'
        }

        if (coverImageTablet.length === 0 && (!isEdit || !formData.content?.cover_image?.tablet)) {
            errors.coverImageTablet = 'Tablet cover image is required'
        }

        // Validate link URL
        const linkUrl = safeTrim(formData.content?.link_url)
        if (!linkUrl) {
            errors.linkUrl = 'Link URL is required'
        } else {
            // Validate URL format
            const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
            if (!urlPattern.test(linkUrl)) {
                errors.linkUrl = 'Please enter a valid URL format (e.g., https://example.com)'
            }
        }

        // Validate city
        if (!safeTrim(formData.content?.location?.city)) {
            errors.city = 'City is required'
        }

        // Validate latitude
        const latValue = safeTrim(formData.content?.location?.lat)
        if (!latValue) {
            errors.lat = 'Latitude is required'
        } else {
            const lat = parseFloat(latValue)
            if (isNaN(lat) || lat < -90 || lat > 90) {
                errors.lat = 'Latitude must be a valid number between -90 and 90'
            }
        }

        // Validate longitude
        const lngValue = safeTrim(formData.content?.location?.lng)
        if (!lngValue) {
            errors.lng = 'Longitude is required'
        } else {
            const lng = parseFloat(lngValue)
            if (isNaN(lng) || lng < -180 || lng > 180) {
                errors.lng = 'Longitude must be a valid number between -180 and 180'
            }
        }

        // Validate valid_to
        if (!safeTrim(formData.valid_to)) {
            errors.validTo = 'Valid To date is required'
        }

        // Validate valid from date
        if (!formData.valid_from) {
            errors.validFrom = 'Valid from date is required'
        }

        return errors
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (blockSubmission)
            return;
        
        const errors = validateForm()
        setValidationErrors(errors)
        
        if (Object.keys(errors).length > 0) {
            return
        }

        setLoading(true)

        // Helper function to transform file object for GraphQL
        const transformFileObject = (fileObj) => {
            if (!fileObj) return null
            // Remove Apollo Client cache fields like __typename
            const { __typename, ...cleanFileObj } = fileObj
            return {
                s3_key: cleanFileObj.s3_key,
                file_name: cleanFileObj.file_name,
                mime_type: cleanFileObj.mime_type,
                size: cleanFileObj.size,
            }
        }

        // Prepare the data for submission
        const submitData = {
            country_code: formData.country_code,
            category_code: formData.category_code,
            content: {
                title: {
                    en: formData.content?.title?.en || '',
                    ar: formData.content?.title?.ar || '',
                },
                description: {
                    en: formData.content?.description?.en?.trim() || 'No description',
                    ar: formData.content?.description?.ar?.trim() || 'لا يوجد وصف',
                },
                link_url: formData.content?.link_url?.trim() || 'https://example.com',
                contact_no: formData.content?.contact_no?.trim() || 'N/A',
                pricing: formData.content?.pricing?.price_from ? {
                    price_from: parseFloat(formData.content.pricing.price_from),
                    currency: formData.content.pricing.currency,
                } : null,
                location: formData.content?.location?.city ? {
                    city: formData.content.location.city,
                    lat: parseFloat(formData.content.location.lat || 0),
                    lng: parseFloat(formData.content.location.lng || 0),
                } : null,
                cover_image: {
                    mobile: transformFileObject(coverImageMobile[0]) || transformFileObject(formData.content?.cover_image?.mobile),
                    tablet: transformFileObject(coverImageTablet[0]) || transformFileObject(formData.content?.cover_image?.tablet),
                },
                images: additionalImages.map(img => {
                    // Always transform the image object to remove __typename
                    if (img && typeof img === 'object' && img.s3_key) {
                        return {
                            mobile: transformFileObject(img),
                            tablet: transformFileObject(img),
                        }
                    }
                    return {
                        mobile: transformFileObject(img),
                        tablet: transformFileObject(img),
                    }
                }),
            },
            valid_from: formData.valid_from,
            valid_to: formData.valid_to,
            is_active: formData.is_active,
        }

        if (isEdit) {
            updateExploreItem({
                variables: {
                    itemId: id,
                    exploreItem: submitData,
                },
            })
        } else {
            createExploreItem({
                variables: {
                    exploreItem: submitData,
                },
            })
        }
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    const handleErrorDialogClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    if (getLoading) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/explore-items')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Explore Items')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                            <div className="sm:col-span-6">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {isEdit ? t('Edit Explore Item') : t('Create Explore Item')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {isEdit ? t('Update the explore item details') : t('Add a new explore item')}
                                </p>
                            </div>

                            {/* Basic Information */}
                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium text-gray-900 ">{t('Basic Information')}</h3>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Country')}
                                    <span className="text-red">*</span>
                                </label>
                                <Select
                                    value={formData.country_code}
                                    onChange={e => {
                                        handleInputChange('country_code', e.target.value)
                                        if (validationErrors.country) {
                                            setValidationErrors(prev => ({ ...prev, country: null }))
                                        }
                                    }}
                                    required
                                >
                                    <option value="">{t('Select Country')}</option>
                                    {availableCountries.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                                {validationErrors.country && (
                                    <p className="text-red text-xs mt-1">{validationErrors.country}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Category')}
                                    <span className="text-red">*</span>
                                </label>
                                <Select
                                    value={formData.category_code}
                                    onChange={e => {
                                        handleInputChange('category_code', e.target.value)
                                        if (validationErrors.category) {
                                            setValidationErrors(prev => ({ ...prev, category: null }))
                                        }
                                    }}
                                    required
                                >
                                    <option value="">{t('Select Category')}</option>
                                    {availableCategories.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                                {validationErrors.category && (
                                    <p className="text-red text-xs mt-1">{validationErrors.category}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Title (English)')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="text"
                                    value={formData.content?.title?.en || ''}
                                    onChange={e => {
                                        handleInputChange('content.title.en', e.target.value)
                                        if (validationErrors.titleEn) {
                                            setValidationErrors(prev => ({ ...prev, titleEn: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.titleEn && (
                                    <p className="text-red text-xs mt-1">{validationErrors.titleEn}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Title (Arabic)')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="text"
                                    value={formData.content?.title?.ar || ''}
                                    onChange={e => {
                                        handleInputChange('content.title.ar', e.target.value)
                                        if (validationErrors.titleAr) {
                                            setValidationErrors(prev => ({ ...prev, titleAr: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.titleAr && (
                                    <p className="text-red text-xs mt-1">{validationErrors.titleAr}</p>
                                )}
                            </div>

                            <div className="sm:col-span-6">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Description (English)')}
                                </label>
                                <Textarea
                                    rows={3}
                                    value={formData.content?.description?.en || ''}
                                    onChange={e => handleInputChange('content.description.en', e.target.value)}
                                />
                            </div>

                            <div className="sm:col-span-6">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Description (Arabic)')}
                                </label>
                                <Textarea
                                    rows={3}
                                    value={formData.content?.description?.ar || ''}
                                    onChange={e => handleInputChange('content.description.ar', e.target.value)}
                                />
                            </div>

                            <div className="sm:col-span-6">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Link URL')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="url"
                                    value={formData.content?.link_url || ''}
                                    onChange={e => {
                                        handleInputChange('content.link_url', e.target.value)
                                        if (validationErrors.linkUrl) {
                                            setValidationErrors(prev => ({ ...prev, linkUrl: null }))
                                        }
                                    }}
                                    placeholder="https://example.com"
                                    required
                                />
                                {validationErrors.linkUrl && (
                                    <p className="text-red text-xs mt-1">{validationErrors.linkUrl}</p>
                                )}
                            </div>

                            <div className="sm:col-span-6">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Contact Number')}
                                </label>
                                <PhoneInput
                                    name="contact_no"
                                    defaultCountry={DEFAULT_COUNTRY}
                                    value={formData.content?.contact_no || ''}
                                    onChange={phone => {
                                        // Set to null if phone is empty or just contains the default country code
                                        const phoneValue = !phone || phone === DEFAULT_COUNTRY_CODE ? null : phone
                                        handleInputChange('content.contact_no', phoneValue)
                                    }}
                                    onBlur={() => {
                                        // Clear the value if it's just the default country code
                                        if (formData.content?.contact_no === DEFAULT_COUNTRY_CODE) {
                                            handleInputChange('content.contact_no', null)
                                        }
                                    }}
                                    placeholder=""
                                    className="block w-full text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                />
                            </div>

                            {/* Pricing Information */}
                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('Pricing Information')}</h3>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Price From')}
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.content?.pricing?.price_from || ''}
                                    onChange={e => handleInputChange('content.pricing.price_from', e.target.value)}
                                />
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Currency')}
                                </label>
                                <Select
                                    value={formData.content?.pricing?.currency || ''}
                                    onChange={e => handleInputChange('content.pricing.currency', e.target.value)}
                                >
                                    <option value="">{t('Select Currency')}</option>
                                    {availableCurrencies.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>

                            {/* Location Information */}
                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('Location Information')}</h3>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('City')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="text"
                                    value={formData.content?.location?.city || ''}
                                    onChange={e => {
                                        handleInputChange('content.location.city', e.target.value)
                                        if (validationErrors.city) {
                                            setValidationErrors(prev => ({ ...prev, city: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.city && (
                                    <p className="text-red text-xs mt-1">{validationErrors.city}</p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Latitude')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="number"
                                    step="any"
                                    value={formData.content?.location?.lat || ''}
                                    onChange={e => {
                                        handleInputChange('content.location.lat', e.target.value)
                                        if (validationErrors.lat) {
                                            setValidationErrors(prev => ({ ...prev, lat: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.lat && (
                                    <p className="text-red text-xs mt-1">{validationErrors.lat}</p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Longitude')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="number"
                                    step="any"
                                    value={formData.content?.location?.lng || ''}
                                    onChange={e => {
                                        handleInputChange('content.location.lng', e.target.value)
                                        if (validationErrors.lng) {
                                            setValidationErrors(prev => ({ ...prev, lng: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.lng && (
                                    <p className="text-red text-xs mt-1">{validationErrors.lng}</p>
                                )}
                            </div>

                            {/* Images */}
                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('Images')}</h3>
                            </div>

                            <div className="sm:col-span-3">
                                <FileAttachment
                                    isRequired={true}
                                    maxLength={1}
                                    uploadLocation="public/explore_items"
                                    title={t('Cover Image (Mobile)')}
                                    onChange={handleCoverImageMobileChange}
                                    attachments={coverImageMobile}
                                    maxSize={5}
                                    id="cover-image-mobile"
                                    name="cover-image-mobile"
                                />
                                {validationErrors.coverImageMobile && (
                                    <p className="text-red text-xs mt-1">{validationErrors.coverImageMobile}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <FileAttachment
                                    isRequired={true}
                                    maxLength={1}
                                    uploadLocation="public/explore_items"
                                    title={t('Cover Image (Tablet)')}
                                    onChange={handleCoverImageTabletChange}
                                    attachments={coverImageTablet}
                                    maxSize={5}
                                    id="cover-image-tablet"
                                    name="cover-image-tablet"
                                />
                                {validationErrors.coverImageTablet && (
                                    <p className="text-red text-xs mt-1">{validationErrors.coverImageTablet}</p>
                                )}
                            </div>

                            <div className="sm:col-span-6">
                                <FileAttachment
                                    isRequired={false}
                                    maxLength={10}
                                    uploadLocation="public/explore_items"
                                    title={t('Additional Images')}
                                    onChange={handleAdditionalImagesChange}
                                    attachments={additionalImages}
                                    maxSize={5}
                                    id="additional-images"
                                    name="additional-images"
                                />
                            </div>

                            {/* Validity and Status */}
                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('Validity and Status')}</h3>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Valid From')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="date"
                                    value={formData.valid_from || ''}
                                    onChange={e => {
                                        handleInputChange('valid_from', e.target.value)
                                        if (validationErrors.validFrom) {
                                            setValidationErrors(prev => ({ ...prev, validFrom: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.validFrom && (
                                    <p className="text-red text-xs mt-1">{validationErrors.validFrom}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Valid To')}
                                    <span className="text-red">*</span>
                                </label>
                                <Input
                                    type="date"
                                    value={formData.valid_to || ''}
                                    onChange={e => {
                                        handleInputChange('valid_to', e.target.value)
                                        if (validationErrors.validTo) {
                                            setValidationErrors(prev => ({ ...prev, validTo: null }))
                                        }
                                    }}
                                    required
                                />
                                {validationErrors.validTo && (
                                    <p className="text-red text-xs mt-1">{validationErrors.validTo}</p>
                                )}
                            </div>

                            <div className="sm:col-span-6">
                                <div className="flex items-center">
                                    <Switch
                                        checked={formData.is_active || false}
                                        onChange={checked => handleInputChange('is_active', checked)}
                                    />
                                    <label className="ml-3 block text-sm font-medium leading-6 text-gray-900">
                                        {t('Active')}
                                    </label>
                                </div>
                            </div>
                            </div>
                        </div>
                        
                    <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-6 sm:px-8 mt-6">
                        <button
                            onClick={() => navigate('/explore-items')}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {loading ? t('Saving...') : t('Save')}
                        </button>
                    </div>
                    </div>
                </form>
            </div>

            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrorDialogClose} />
            )}
        </>
    )
}
